import { useAtom } from 'jotai';
import { Skeleton } from 'primereact/skeleton';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fundAnalyticsDataAtom } from '~/atoms/fundAnalyticsAtom';
import { WidgetActions } from '~/components/ui/common/WidgetActions';

// Finance-inspired colors from CSS variables
const CHART_COLORS = [
  'var(--chart-color-profit)', // Green (profit)
  'var(--chart-color-stable)', // Blue (stable)
  'var(--chart-color-caution)', // Amber (caution)
  'var(--chart-color-luxury)', // Purple (luxury)
  'var(--chart-color-loss)', // Red (loss)
  'var(--chart-color-tech)', // Cyan (tech)
  'var(--chart-color-energy)', // Orange (energy)
  'var(--chart-color-growth)', // Light Green (growth)
  'var(--chart-color-conservative)', // Indigo (conservative)
  'var(--chart-color-disruptive)', // Pink (disruptive)
];

type AggrGrossMoicLineChartProps = {
  widgetId?: string;
  handleRemoveWidget?: (widgetId: string) => void;
};

export default function AggrGrossMoicLineChart({
  handleRemoveWidget,
  widgetId,
}: AggrGrossMoicLineChartProps) {
  const [fundAnalyticsState] = useAtom(fundAnalyticsDataAtom);

  if (fundAnalyticsState.loading) {
    return (
      <div className="h-full">
        <Skeleton className="h-full"></Skeleton>
      </div>
    );
  }

  const removeWidget = () => {
    widgetId && handleRemoveWidget && handleRemoveWidget(widgetId);
  };

  if (fundAnalyticsState.error) {
    return (
      <div className="flex flex-column h-full justify-content-center align-items-center hover-container">
        <div className="flex justify-content-end w-full h-2rem">
          <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
        </div>
        <div className="align-items-center flex h-full p-error  mb-6">
          Something went wrong.
        </div>
      </div>
    );
  }

  return (
    <div className="hover-container">
      <div className="mb-4 w-full flex justify-content-between align-items-center">
        <h3 className="text-lg font-semibold mb-0">Aggregate Gross MOIC</h3>
        <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
      </div>

      <div className="h-20rem">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={500}
            height={300}
            data={fundAnalyticsState.grossMOICData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="formattedDate" />
            <YAxis domain={[0, 2.5]} />

            <Tooltip />
            <Legend />
            {fundAnalyticsState.fundNames.map((name, index) => {
              return (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={name}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
